import { makeStyles, shorthands, tokens } from "@fluentui/react-components";
import { Colors, WorkspaceSizeThresholds } from "../constants";
import { ellipsis, interactionBarShadow } from "./commonStyle";

export const useInteractionBarCommonStyle = makeStyles({
	squareColoredButton: {
		color: tokens.colorNeutralForegroundInverted,
		minWidth: "50px",
		height: "50px",
		":hover": {
			color: tokens.colorNeutralForegroundInverted,
			":active": {
				backgroundColor: tokens.colorPaletteRedForeground2,
				color: tokens.colorNeutralForegroundInverted,
			},
		},
	},
	redColor: {
		backgroundColor: tokens.colorPaletteRedBackground3,
		...shorthands.borderColor(tokens.colorPaletteRedBackground3),

		":hover": {
			backgroundColor: tokens.colorPaletteRedForeground1,
			...shorthands.borderColor(tokens.colorPaletteRedForeground1),
		},
	},
	greenColor: {
		backgroundColor: Colors.ButtonGreenBackground,
		...shorthands.borderColor(Colors.ButtonGreenBackground),

		":hover": {
			backgroundColor: Colors.ButtonGreenBackgroundHover,
			...shorthands.borderColor(Colors.ButtonGreenBackgroundHover),
		},
	},
	greenColorDisabled: {
		backgroundColor: tokens.colorPaletteGreenBackground2,
		...shorthands.borderColor(tokens.colorPaletteGreenBackground2),

		":hover": {
			backgroundColor: tokens.colorPaletteGreenBackground2,
			...shorthands.borderColor(tokens.colorPaletteGreenBackground2),
		},
	},
	disabled: {
		opacity: 0.7,
		transition: 'opacity 0.3s ease',
	},
	squareRedIconButton: {
		color: tokens.colorPaletteRedBackground3,
		minWidth: "50px",
		height: "50px",
	},
	interactionBar: {
		display: "flex",
		backgroundColor: tokens.colorNeutralBackground1,
		marginLeft: "16px",
		marginRight: "16px",
		marginTop: "13px",
		flexShrink: 0,
		...interactionBarShadow(),
		borderRadius: tokens.borderRadiusMedium,
		justifyContent: "space-between",

		[`@container workspacePanel (width > ${WorkspaceSizeThresholds.Medium}px)`]:
			{
				height: "60px",
				flexDirection: "row",
			},

		[`@container workspacePanel (width <= ${WorkspaceSizeThresholds.Medium}px)`]:
			{
				flexDirection: "column",
			},

		[`@media (max-width: 440px)`]: {
			margin: "0px",
			borderRadius: "0",
		},
	},
	divider: {
		height: "100%",

		[`@container workspacePanel (width <= ${WorkspaceSizeThresholds.Medium}px)`]:
			{
				display: "none",
			},
	},
	interactionIcon: {
		marginLeft: "14px",
		marginRight: "16px",
	},
	interactionIconColor: {
		color: tokens.colorNeutralForeground3,
	},
	iconCircle: {
		backgroundColor: tokens.colorNeutralBackground4Hover,
		borderRadius: "50%",
		width: "40px",
		minWidth: "40px",
		height: "40px",
		boxSizing: "border-box",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		flexShrink: 0,
	},
	interactionText: {
		color: tokens.colorNeutralBackgroundStatic,
		// `hidden auto`, not `hidden scroll`: `scroll` permanently reserves a ~15px vertical
		// scrollbar gutter even when nothing overflows. This class is merged onto both the
		// action container and the button row, so two reserved gutters (+ the 5px marginRight)
		// pushed the close button ~35px off the right edge — far more than the 5px top/bottom
		// gap. `auto` only reserves the gutter when content actually overflows, so the right
		// padding now matches top/bottom (as it already does in the wrap-up bar).
		overflow: "hidden auto",
	},
	interactionTimerAndButtonsContainer: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		paddingLeft: "20px",
		[`@container workspacePanel (width > ${WorkspaceSizeThresholds.Medium}px)`]:
			{
				flexDirection: "row",
			},

		[`@container workspacePanel (width <= ${WorkspaceSizeThresholds.Medium}px)`]:
			{
				flexDirection: "row",
				justifyContent: "flex-end",
				"& > div": {
					height: "60px",
					alignItems: "center",
				},
			},

		[`@container workspacePanel (width <= ${WorkspaceSizeThresholds.Small}px)`]:
			{
				flexDirection: "column",
				alignItems: "center",
			},
	},
	interactionButtons: {
		marginRight: "5px",
		flexShrink: 0,
	},
	timer: {
		marginRight: "15px",
		color: tokens.colorNeutralForeground1,
		alignItems: "center",
	},
	interactionAction: {
		marginRight: "17px",
		textAlign: "right",
		...shorthands.overflow("hidden"),
		color: tokens.colorNeutralForeground3,
		...shorthands.flex(1, 1, "auto"),
		minWidth: 0,
	},
	interactionActionContainer: {
		...shorthands.flex(1, 1, "auto"),
		minWidth: 0,

		[`@container workspacePanel (width < ${WorkspaceSizeThresholds.Small})`]: {
			width: "100%",
			justifyContent: "center",
		},
	},
	actionText: {
		fontSize: tokens.fontSizeBase200,
		...ellipsis(),
	},
	onHoldText: {
		backgroundColor: tokens.colorNeutralBackground1Hover,
		...shorthands.padding("7px", "10px"),
	},
	iconRecordStop: {
		color: tokens.colorPaletteRedBackground3,
	},
	queueName: {
		maxWidth: "250px",
	},
});
